import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Chat24Regular,
    ArrowDownload16Regular,
    MoreHorizontal16Regular,
    Dismiss12Regular,
    Warning12Filled,
    CheckmarkCircle12Filled,
    Info12Regular,
    ChevronRight16Regular,
} from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { toast } from 'react-toastify';
import { ObservationFormSidebar } from '../../Observations';
import { PostInformationRequestSidebar } from '../../IRL';
import AskAiQuickCreateSidebar from '../AskAiQuickCreateSidebar';
import InsightsNewEvidenceBanner from './InsightsNewEvidenceBanner';
import InsightsHistoricalVersionBanner from './InsightsHistoricalVersionBanner';
import { useInsightsReadOnly } from './InsightsReadOnlyContext';
import moduleStyles from './intelligenceInsightsModules.module.css';
import { saveInsightsModuleToJournal, buildHighlightsCardJournalBody } from '../../../utils/askLensJournal';
import { isInsightsReadOnlyBlocked } from '../../../utils/intelligenceInsightsActions';
import HighlightsEvidencePanel from './HighlightsEvidencePanel';
import ModuleEmptyState from './ModuleEmptyState';
import styles from './intelligenceInsightsHighlights.module.css';

const RAG_CLASS = {
    amber: styles.ragAmber,
    green: styles.ragGreen,
    red: styles.ragRed,
    grey: styles.ragGrey,
};

const MODULE_NAV_KEYS = {
    open_key_findings: 'key_findings',
    open_risks_signals: 'risks_signals',
    open_evidence_follow_ups: 'evidence_follow_ups',
    open_management_summary: 'management_summary',
};

const ROUTE_BY_ACTION = {
    open_key_findings: '/observations',
    open_risks_signals: '/raid',
    open_evidence_follow_ups: '/irl',
    open_irl: '/irl',
    open_raid_item: '/raid',
    open_observation: '/observations',
};

function stripCitations(text) {
    return String(text || '')
        .replace(/(?:\s*\[[^\]]+\])+\s*$/g, '')
        .replace(/\s*\[[^\]]+\]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

function cardPrefillText(card) {
    if (!card) return '';
    return [card.headline, card.conclusion, card.whyItMatters].filter(Boolean).join('\n\n');
}

function RagPill({ rag, label }) {
    const cls = RAG_CLASS[rag] || styles.ragGrey;
    return (
        <span className={`${styles.ragPill} ${cls}`}>
            <span className={styles.ragPillDot} aria-hidden />
            {label || rag}
        </span>
    );
}

function HighlightCard({ card, onAction }) {
    const primary = card.primaryAction;
    const secondary = card.secondaryActions || [];
    const detailsAction =
        secondary.find(a => a.key === 'view_details') || {
            key: 'view_details',
            label: 'View details',
            actionType: 'view_details',
        };
    // Brief §6 / Figma: card face is mutually exclusive — never Create IRL + View evidence together.
    // Grey/limited → Create IRL + View details; assessed → View evidence + View details.
    const isInsufficient = card.rag === 'grey';
    const createIrlAction =
        primary?.key === 'create_irl'
            ? primary
            : {
                  key: 'create_irl',
                  label: 'Create IRL request',
                  actionType: 'create_irl',
              };
    const evidenceAction =
        (primary?.key === 'view_evidence' ? primary : null) || {
            key: 'view_evidence',
            label: 'View evidence',
            actionType: 'open_evidence_panel',
        };
    const confidenceLine = (() => {
        const conf = card.confidence
            ? `${String(card.confidence).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())} confidence`
            : null;
        const cov = card.evidenceCoverage
            ? `${String(card.evidenceCoverage).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())} evidence`
            : null;
        return [conf, cov].filter(Boolean).join(' · ');
    })();

    return (
        <article className={styles.card}>
            <div className={styles.cardTop}>
                <RagPill
                    rag={card.rag}
                    label={isInsufficient ? 'Insufficient evidence' : card.ragLabel}
                />
                <button type="button" className={styles.cardMenuBtn} aria-label="More">
                    <MoreHorizontal16Regular />
                </button>
            </div>
            <p className={styles.areaLabel}>{card.displayLabel}</p>
            <h3 className={styles.headline}>{card.headline}</h3>
            <p className={styles.conclusion}>{card.conclusion}</p>
            {card.unavailableSourceNote ? (
                <p className={styles.cardUnavailable}>
                    <Warning12Filled aria-hidden />
                    {card.unavailableSourceNote}
                </p>
            ) : null}
            <div className={styles.whyBlock}>
                <p className={styles.whyLabel}>Why it matters</p>
                <p className={styles.whyText}>{card.whyItMatters}</p>
                {isInsufficient && confidenceLine ? (
                    <p className={styles.cardConfidence}>{confidenceLine}</p>
                ) : null}
                <div className={styles.cardActions}>
                    {isInsufficient ? (
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={() => onAction?.(createIrlAction, card)}>
                            {createIrlAction.label}
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={styles.linkPrimary}
                            onClick={() => onAction?.(evidenceAction, card)}>
                            {evidenceAction.label}
                        </button>
                    )}
                    <button
                        type="button"
                        className={styles.btnGhost}
                        onClick={() => onAction?.(detailsAction, card)}>
                        {detailsAction.label}
                    </button>
                </div>
            </div>
        </article>
    );
}

function DetailDrawer({ open, card, onClose, onAction }) {
    if (!card) return null;
    const detail = card.detail || {};
    const fullFinding = stripCitations(detail.fullFinding || card.conclusion || '');
    const rationale = stripCitations(detail.assessmentRationale || '');
    const sourceCount = Number(card.sourceCount || 0);
    const positives = detail.materialPositives || [];
    const concerns = detail.materialConcerns || [];
    const limitations = detail.assessmentLimitations || [];
    const riskIds = card.relatedRiskIds || [];
    const matterIds = card.relatedEvidenceMatterIds || [];

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={typeof document !== 'undefined' ? document.body : undefined}
            style={{ width: 440, maxWidth: '100vw' }}
            onOpenChange={(_, data) => {
                if (!data.open) onClose?.();
            }}>
            <DrawerBody className={styles.detailDrawerBody}>
                <div className={styles.evidenceRoot}>
                    <div className={styles.detailHeader}>
                        <h2 className={styles.detailTitle}>{card.displayLabel} - Detail</h2>
                        <button
                            type="button"
                            className={styles.drawerClose}
                            onClick={onClose}
                            aria-label="Close">
                            <Dismiss12Regular />
                        </button>
                    </div>

                    <div className={styles.detailBody}>
                        <section className={styles.evidenceSection}>
                            <p className={styles.drawerSectionTitle}>Full finding</p>
                            <p className={styles.drawerText}>{fullFinding}</p>
                        </section>

                        {rationale ? (
                            <section className={styles.evidenceSection}>
                                <p className={styles.drawerSectionTitle}>Assessment rationale</p>
                                <p className={styles.drawerText}>{rationale}</p>
                            </section>
                        ) : null}

                        {positives.length ? (
                            <section className={styles.evidenceSection}>
                                <p className={styles.drawerSectionTitle}>Material positives</p>
                                <ul className={`${styles.bulletList} ${styles.bulletListPositive}`}>
                                    {positives.map(item => (
                                        <li key={item} className={styles.bulletItem}>
                                            <CheckmarkCircle12Filled
                                                className={styles.bulletIconPositive}
                                                aria-hidden
                                            />
                                            <span>{stripCitations(item)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ) : null}

                        {concerns.length ? (
                            <section className={styles.evidenceSection}>
                                <p className={styles.drawerSectionTitle}>Material concerns</p>
                                <ul className={`${styles.bulletList} ${styles.bulletListConcern}`}>
                                    {concerns.map(item => (
                                        <li key={item} className={styles.bulletItem}>
                                            <Warning12Filled
                                                className={styles.bulletIconConcern}
                                                aria-hidden
                                            />
                                            <span>{stripCitations(item)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ) : null}

                        {limitations.length ? (
                            <section className={styles.evidenceSection}>
                                <p className={styles.drawerSectionTitle}>Assessment limitations</p>
                                <ul className={styles.limitationList}>
                                    {limitations.map(item => (
                                        <li key={item} className={styles.limitationItem}>
                                            <Info12Regular
                                                className={styles.bulletIconInfo}
                                                aria-hidden
                                            />
                                            <span className={styles.limitationText}>
                                                {stripCitations(item)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ) : null}

                        {riskIds.length ? (
                            <section className={styles.evidenceSection}>
                                <p className={styles.drawerSectionTitle}>Risks & Signals</p>
                                <div className={styles.chipRow}>
                                    {riskIds.map(id => (
                                        <button
                                            key={id}
                                            type="button"
                                            className={styles.chip}
                                            onClick={() =>
                                                onAction?.(
                                                    {
                                                        key: 'open_risks_signals',
                                                        label: 'Open Risks & Signals',
                                                        actionType: 'open_risks_signals',
                                                    },
                                                    card,
                                                )
                                            }>
                                            <span>{id}</span>
                                            <ChevronRight16Regular
                                                className={styles.chipChevron}
                                                aria-hidden
                                            />
                                        </button>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {matterIds.length ? (
                            <section className={styles.evidenceSection}>
                                <p className={styles.drawerSectionTitle}>Evidence & Follow-Ups</p>
                                <div className={styles.chipRow}>
                                    {matterIds.map(id => (
                                        <button
                                            key={id}
                                            type="button"
                                            className={styles.chip}
                                            onClick={() =>
                                                onAction?.(
                                                    {
                                                        key: 'open_evidence_follow_ups',
                                                        label: 'Open Evidence & Follow-Ups',
                                                        actionType: 'open_evidence_follow_ups',
                                                    },
                                                    card,
                                                )
                                            }>
                                            <span>{id}</span>
                                            <ChevronRight16Regular
                                                className={styles.chipChevron}
                                                aria-hidden
                                            />
                                        </button>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        <section className={styles.evidenceSection}>
                            <p className={styles.drawerSectionTitle}>Sources</p>
                            <p className={styles.sourcesLine}>
                                {sourceCount} source{sourceCount === 1 ? '' : 's'} reviewed
                            </p>
                        </section>
                    </div>

                    <div className={styles.drawerFooter}>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={() =>
                                onAction?.(
                                    {
                                        key: 'view_evidence',
                                        label: 'View evidence',
                                        actionType: 'open_evidence_panel',
                                    },
                                    card,
                                )
                            }>
                            View evidence
                        </button>
                        <button
                            type="button"
                            className={styles.btnOutline}
                            onClick={() =>
                                onAction?.(
                                    {
                                        key: 'create_irl',
                                        label: 'Create IRL',
                                        actionType: 'create_irl',
                                    },
                                    card,
                                )
                            }>
                            Create IRL
                        </button>
                        <button
                            type="button"
                            className={styles.btnOutline}
                            onClick={() =>
                                onAction?.(
                                    {
                                        key: 'add_to_raid',
                                        label: 'Add to RAID',
                                        actionType: 'add_to_raid',
                                    },
                                    card,
                                )
                            }>
                            Add to RAID
                        </button>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

/**
 * Intelligence Insights Highlights screen (screens.highlights).
 */
const IntelligenceInsightsHighlights = ({
    projectId,
    data,
    loading,
    generating,
    error,
    onRetry,
    onGenerateAnalysis,
    onGoToOverview,
    onNewEvidenceAction,
    onAskAi,
    onExport,
    exportEnabled = false,
    onOpenModule,
    onReviewFailedSource,
    historicalVersion = null,
    onExitHistoricalView = null,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const notesState = useSelector(state => state?.dashboard?.notes);
    const readOnly = useInsightsReadOnly();

    const [detailCard, setDetailCard] = useState(null);
    const [evidenceArea, setEvidenceArea] = useState(null);
    const [sidebarContent, setSidebarContent] = useState('');
    const [showIrlSidebar, setShowIrlSidebar] = useState(false);
    const [showRaidSidebar, setShowRaidSidebar] = useState(false);
    const [showObsSidebar, setShowObsSidebar] = useState(false);

    const actionContextCard = useMemo(() => detailCard, [detailCard]);

    const closeDrawers = () => {
        setDetailCard(null);
        setEvidenceArea(null);
    };

    const handleCardAction = async (action, card) => {
        if (!action || isInsightsReadOnlyBlocked(readOnly, action)) return;
        const key = action.key || action.actionType;

        if (key === 'view_details') {
            setEvidenceArea(null);
            setDetailCard(card);
            return;
        }
        if (key === 'view_evidence' || key === 'open_evidence_panel') {
            setDetailCard(null);
            setEvidenceArea(card.area);
            return;
        }
        if (key === 'create_irl') {
            setSidebarContent(cardPrefillText(card));
            closeDrawers();
            setShowIrlSidebar(true);
            return;
        }
        if (key === 'add_to_raid' || key === 'add_raid') {
            setSidebarContent(cardPrefillText(card));
            closeDrawers();
            setShowRaidSidebar(true);
            return;
        }
        if (key === 'create_observation') {
            setSidebarContent(cardPrefillText(card));
            closeDrawers();
            setShowObsSidebar(true);
            return;
        }
        if (key === 'save_to_journal') {
            try {
                await saveInsightsModuleToJournal({
                    dispatch,
                    notesState,
                    moduleLabel: 'Highlights',
                    headline: card.headline || card.displayLabel || card.area || '',
                    bodyText: buildHighlightsCardJournalBody(card),
                });
                toast.success('Saved to Journal');
            } catch {
                toast.error('Could not save to Journal');
            }
            return;
        }

        const moduleKey = MODULE_NAV_KEYS[key];
        if (moduleKey && onOpenModule) {
            closeDrawers();
            onOpenModule(moduleKey);
            return;
        }

        const route = ROUTE_BY_ACTION[key] || action.route;
        if (route) {
            closeDrawers();
            navigate(route);
            return;
        }
    };

    const handleEmptyPrimary = () => {
        onGoToOverview?.();
    };

    const showEmpty =
        !loading &&
        !error &&
        !data?.isPopulated &&
        (data?.emptyState || data?.isNotGenerated || data?.isFailed || data?.isGenerating);

    return (
        <div className={`${styles.highlightsRoot} ${readOnly ? moduleStyles.moduleRootReadOnly : ''}`}>
            <header className={styles.header}>
                <div className={styles.headerTop}>
                    <div>
                        <h1 className={styles.title}>{data?.title || 'Highlights'}</h1>
                        <p className={styles.subtitle}>{data?.subtitle}</p>
                        {data?.metaLine ? <p className={styles.meta}>{data.metaLine}</p> : null}
                    </div>
                    <div className={styles.headerActions}>
                        <button
                            type="button"
                            className={`${styles.btnGhost} ${
                                readOnly ? moduleStyles.headerActionDisabled : ''
                            }`}
                            onClick={onAskAi}
                            disabled={readOnly}>
                            <Chat24Regular />
                            Ask Lens
                        </button>
                        {exportEnabled && onExport ? (
                            <button
                                type="button"
                                className={styles.btnGhost}
                                onClick={onExport}>
                                <ArrowDownload16Regular />
                                Export PDF
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className={`${styles.cardMenuBtn} ${
                                readOnly ? moduleStyles.headerActionDisabled : ''
                            }`}
                            aria-label="More"
                            disabled={readOnly}>
                            <MoreHorizontal16Regular />
                        </button>
                    </div>
                </div>
            </header>

            {readOnly && historicalVersion ? (
                <InsightsHistoricalVersionBanner
                    version={historicalVersion}
                    onReturnToCurrent={onExitHistoricalView}
                />
            ) : null}

            {error ? (
                <div className={styles.body}>
                    <ModuleEmptyState
                        moduleKey="highlights"
                        variant="failed"
                        error={error}
                        onRetry={onRetry}
                        onGenerateAnalysis={onGenerateAnalysis}
                        onGoToOverview={onGoToOverview}
                    />
                </div>
            ) : null}

            {!error ? (
                <div className={styles.body}>
                    {data?.warnings?.length ? (
                        <div className={styles.warningBanner} role="status">
                            <div className={styles.warningTitleRow}>
                                <Warning12Filled className={styles.warningTitleIcon} aria-hidden />
                                <p className={styles.warningTitle}>
                                    {data.warnings[0]?.title || 'Analysis complete with warnings'}
                                </p>
                            </div>
                            <p className={styles.warningMessage}>
                                {data.warnings[0]?.message ||
                                    'Some sources were unavailable during assessment.'}
                            </p>
                            {data.warnings[0]?.affected ? (
                                <p className={styles.warningAffected}>
                                    {data.warnings[0].affected}
                                </p>
                            ) : null}
                            <div className={styles.warningActions}>
                                <button
                                    type="button"
                                    className={styles.btnGhost}
                                    onClick={() => {
                                        if (onReviewFailedSource) {
                                            onReviewFailedSource();
                                            return;
                                        }
                                        if (onOpenModule) onOpenModule('overview');
                                        else navigate('/dataroom');
                                    }}>
                                    {data.warnings[0]?.actionLabel || 'Review failed source'}
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {data?.newEvidenceBanner && !readOnly ? (
                        <InsightsNewEvidenceBanner
                            banner={data.newEvidenceBanner}
                            onAction={onNewEvidenceAction}
                        />
                    ) : null}

                    {loading && !data?.isPopulated ? (
                        <ModuleEmptyState moduleKey="highlights" loading />
                    ) : null}

                    {showEmpty ? (
                        <ModuleEmptyState
                            moduleKey="highlights"
                            emptyState={data?.emptyState}
                            status={data?.status}
                            variant={
                                data?.isFailed || data?.emptyState?.type === 'failed'
                                    ? 'failed'
                                    : 'not_generated'
                            }
                            generating={generating}
                            onGoToOverview={onGoToOverview}
                            onGenerateAnalysis={onGenerateAnalysis}
                            onRetry={() =>
                                data?.isFailed ? onGenerateAnalysis?.() : onRetry?.()
                            }
                        />
                    ) : null}

                    {data?.isPopulated ? (
                        <div className={styles.cardsGrid}>
                            {data.cards.map(card => (
                                <HighlightCard key={card.area} card={card} onAction={handleCardAction} />
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}

            <DetailDrawer
                open={Boolean(detailCard)}
                card={detailCard}
                onClose={() => setDetailCard(null)}
                onAction={handleCardAction}
            />
            <HighlightsEvidencePanel
                open={Boolean(evidenceArea)}
                projectId={projectId}
                area={evidenceArea}
                onClose={() => setEvidenceArea(null)}
                onOpenModule={onOpenModule}
                onAction={(action, panelCard) => {
                    const card =
                        panelCard ||
                        data?.cards?.find(c => c.area === evidenceArea) ||
                        actionContextCard;
                    if (!card) return;
                    void handleCardAction(action, card);
                }}
            />

            <PostInformationRequestSidebar
                show={showIrlSidebar}
                setShow={show => {
                    setShowIrlSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultRequest={() => sidebarContent || ''}
            />
            <AskAiQuickCreateSidebar
                show={showRaidSidebar}
                setShow={show => {
                    setShowRaidSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                kind="raid"
                defaultText={() => sidebarContent || ''}
            />
            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultObservation={sidebarContent || ''}
            />
        </div>
    );
};

export default IntelligenceInsightsHighlights;
